/**
 * 매물탐색 마커 클래스
 *
 * 피그마 기준 마커 타입:
 * 1. 단일 매물: 카드형 (왼쪽 그라디언트 바 + 아이콘 + 거래유형/가격/면적)
 * 2. 클러스터: 반투명 원 (rgba(31,73,104,0.6)) + 매물 수
 *
 * 클러스터 크기: 2-9개 → 36px, 10-99개 → 44px, 100+ → 54px
 * 클러스터 폰트: Gmarket Sans Bold, 13-14px
 */

// PC legacy 마커 (fallback)
export const ExplorerMarkerLegacy = window.ExplorerMarker;

// to_area: 레거시 전역 함수 — .to-area-field span 생성, 맵 툴 평/m² 전환 시 자동 연동
function toArea(val, decimals) {
  if (typeof window.to_area === "function") return window.to_area(val, decimals);
  return `${Math.round(val)}㎡`;
}

// ── 매물 유형별 색상 + 아이콘 매핑 (피그마 확정) ──

const MARKER_STYLES = {
  // e_rand_type → { from, to, icon }
  0:  { from: "#43d3b0", to: "#0ec79a", icon: "apartment" },     // 아파트
  1:  { from: "#43d3b0", to: "#0ec79a", icon: "apartment" },     // 주상복합
  2:  { from: "#4bb4f7", to: "#189ff5", icon: "officetel" },     // 오피스텔
  3:  { from: "#f7c438", to: "#f5b300", icon: "detached" },      // 단독
  4:  { from: "#f7c438", to: "#f5b300", icon: "detached" },      // 다가구
  5:  { from: "#f7c438", to: "#f5b300", icon: "detached" },      // 전원주택
  6:  { from: "#bc69f7", to: "#a93ff5", icon: "store" },         // 상가주택
  7:  { from: "#ff7444", to: "#ff4d0f", icon: "multi" },         // 연립
  8:  { from: "#ff7444", to: "#ff4d0f", icon: "multi" },         // 다세대
  10: { from: "#bc69f7", to: "#a93ff5", icon: "store" },         // 상가점포
  11: { from: "#bc69f7", to: "#a93ff5", icon: "store" },         // 사무실
  12: { from: "#7276f3", to: "#4a4ff0", icon: "commercial" },    // 상업용건물
  15: { from: "#fb50a5", to: "#fa1e8c", icon: "hotel" },         // 숙박시설
  16: { from: "#99d244", to: "#7cc50f", icon: "land" },          // 토지
  17: { from: "#cf8a50", to: "#c2691f", icon: "factory" },       // 공장
  18: { from: "#7276f3", to: "#4a4ff0", icon: "commercial" },    // 지식산업센터
  19: { from: "#cf8a50", to: "#c2691f", icon: "factory" },       // 창고
  20: { from: "#4bb4f7", to: "#189ff5", icon: "officetel" },      // 생활숙박시설
};

const DEFAULT_STYLE = { from: "#7276f3", to: "#4a4ff0", icon: "commercial" };

function getMarkerStyle(estateType) {
  return MARKER_STYLES[estateType] || DEFAULT_STYLE;
}

// ── 아이콘 SVG (16x16, white, inline) ──

const ICONS = {
  // 아파트 (241024: building-05)
  apartment: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.559 1.333H4.108c-.352 0-.655 0-.905.02-.263.022-.526.07-.778.198a1.667 1.667 0 0 0-.874.874c-.128.252-.176.515-.197.778-.02.25-.02.553-.02.905V13.333c-.368 0-.667.299-.667.667s.299.667.667.667h13.333c.368 0 .667-.299.667-.667s-.299-.667-.667-.667V9.441c0-.352 0-.655-.02-.905a1.795 1.795 0 0 0-.198-.778 1.667 1.667 0 0 0-.874-.874 1.795 1.795 0 0 0-.778-.198c-.25-.02-.553-.02-.905-.02H9.333V4.108c0-.352 0-.655-.02-.905a1.795 1.795 0 0 0-.198-.778 1.667 1.667 0 0 0-.874-.874 1.795 1.795 0 0 0-.778-.198c-.25-.02-.553-.02-.905-.02zM3.333 4.667a.667.667 0 0 1 .667-.667h2.667a.667.667 0 1 1 0 1.333H4a.667.667 0 0 1-.667-.666zm0 2.666A.667.667 0 0 1 4 6.667h2.667a.667.667 0 1 1 0 1.333H4a.667.667 0 0 1-.667-.667zM4 9.333a.667.667 0 1 0 0 1.334h2.667a.667.667 0 1 0 0-1.334H4z" fill="white"/></svg>`,
  // 오피스텔 (241133: building-04 변형)
  officetel: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M9.893 1.332h.904c.351 0 .655 0 .904.02.264.022.526.07.778.198.376.192.683.498.874.874.129.252.176.515.198.778.02.25.02.553.02.905v9.225h.666a.667.667 0 1 1 0 1.333H2.667a.667.667 0 1 1 0-1.333h.666V4.106c0-.351 0-.654.02-.904.022-.264.07-.526.198-.778a1.667 1.667 0 0 1 .874-.874c.252-.129.515-.176.778-.198.25-.02.553-.02.904-.02h3.786zm-2.293 9.715c-.16 0-.24 0-.302.031a.333.333 0 0 0-.127.127c-.031.061-.031.141-.031.302v1.83h1.716v-1.83c0-.161 0-.241-.03-.302a.333.333 0 0 0-.128-.127c-.06-.031-.141-.031-.301-.031h-.797zM6 8.332a.667.667 0 0 0 0 1.333h1.224V8.332H6zm2.776 1.333H10a.667.667 0 1 0 0-1.333H8.776v1.333zM6 5.665a.667.667 0 1 0 0 1.334h1.224V5.665H6zm2.776 1.334H10a.667.667 0 1 0 0-1.334H8.776v1.334zM6 2.999a.667.667 0 0 0 0 1.333h1.224V2.999H6zm2.776 1.333H10a.667.667 0 1 0 0-1.333H8.776v1.333z" fill="white"/></svg>`,
  // 단독/다가구 (241032: home-03)
  detached: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.66.877a1 1 0 0 1 .68 0c.258.068.477.234.651.366l.049.037.404.303c.051.038.103.077.155.116l.08.061 5.387 4.04a.667.667 0 1 1-.8 1.067L14 6.667v5.225c0 .352 0 .655-.02.905a1.795 1.795 0 0 1-.198.778 1.667 1.667 0 0 1-.874.874c-.252.128-.515.176-.778.198-.25.02-.553.02-.905.02H4.774c-.352 0-.655 0-.905-.02a1.795 1.795 0 0 1-.778-.198 1.667 1.667 0 0 1-.874-.874 1.795 1.795 0 0 1-.198-.778c-.02-.25-.02-.553-.02-.905V6.667l-.267.2a.667.667 0 1 1-.8-1.067l5.387-4.04.08-.06c.053-.04.104-.078.155-.117l.404-.303.049-.037a2.17 2.17 0 0 1 .651-.366zM6 9.067v4.266h4V9.067c0-.374 0-.56-.073-.703a.667.667 0 0 0-.291-.291C9.493 8 9.307 8 8.933 8H7.067c-.374 0-.56 0-.703.073a.667.667 0 0 0-.291.291C6 8.507 6 8.693 6 9.067z" fill="white"/></svg>`,
  // 상가/사무실 (241029: building-02)
  store: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4.108 1.333h7.784c.352 0 .655 0 .905.02.263.022.526.07.778.198.376.192.682.498.874.874.128.252.176.515.198.778.02.25.02.553.02.905v.559c0 .617-.665 1.333-1.778 1.333S11.111 5.284 11.111 4.667a.667.667 0 0 0-1.333 0C9.778 5.284 9.113 6 8 6s-1.778-.716-1.778-1.333a.667.667 0 0 0-1.333 0C4.889 5.284 4.224 6 3.111 6S1.333 5.284 1.333 4.667v-.559c0-.352 0-.655.02-.905.022-.263.07-.526.198-.778a1.667 1.667 0 0 1 .874-.874c.252-.128.515-.176.778-.198.25-.02.553-.02.905-.02z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M12.889 7.333c.633 0 1.257-.171 1.778-.478v5.037c0 .352 0 .655-.02.905a1.795 1.795 0 0 1-.198.778 1.667 1.667 0 0 1-.874.874 1.795 1.795 0 0 1-.778.198c-.25.02-.553.02-.905.02H4.108c-.352 0-.655 0-.905-.02a1.795 1.795 0 0 1-.778-.198 1.667 1.667 0 0 1-.874-.874 1.795 1.795 0 0 1-.198-.778c-.02-.25-.02-.553-.02-.905V6.855c.52.307 1.144.478 1.778.478.937 0 1.852-.375 2.444-1.009.593.634 1.508 1.01 2.445 1.01s1.852-.376 2.444-1.01c.593.634 1.508 1.01 2.445 1.01zM7.067 9.333h1.866c.374 0 .56 0 .703.073.125.064.228.166.291.291.073.143.073.33.073.703v2.933H6v-2.933c0-.374 0-.56.073-.703a.667.667 0 0 1 .291-.291c.143-.073.33-.073.703-.073z" fill="white"/></svg>`,
  // 연립/다세대 (241165: 2채 집)
  multi: `<svg width="16" height="16" viewBox="0 0 15.02 11.86" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M4.637.038a.533.533 0 0 1 .45 0c.171.058.316.2.432.314l.032.031.268.26c.034.033.068.066.103.1l.053.052 3.571 3.465a.413.413 0 0 1-.412.815L8.84 5.003v4.482c0 .302 0 .562-.013.776a1.9 1.9 0 0 1-.131.587 1.333 1.333 0 0 1-.58.75c-.168.11-.341.151-.516.17a5.05 5.05 0 0 1-.6 0H2.723a5.05 5.05 0 0 1-.6-.017 1.13 1.13 0 0 1-.515-.17 1.333 1.333 0 0 1-.58-.75 1.9 1.9 0 0 1-.131-.587 10.8 10.8 0 0 1-.013-.776V5.003l-.177.172a.413.413 0 0 1-.412-.815L3.748.795l.053-.052c.035-.034.069-.067.103-.1l.268-.26.032-.031c.116-.114.261-.256.433-.314zm-.1 7.024v3.659h2.651V7.062c0-.32 0-.48-.048-.603a.667.667 0 0 0-.193-.25c-.107-.063-.231-.063-.48-.063H4.725c-.248 0-.372 0-.48.063a.667.667 0 0 0-.193.25c-.048.122-.048.283-.048.603z" fill="white"/><path d="M9.931.038a.533.533 0 0 1 .45 0c.171.058.317.201.432.314l.032.031.268.26.103.1.054.052 3.571 3.466a.413.413 0 0 1-.412.814l-.177-.171v4.482c0 .301 0 .562-.014.776a1.9 1.9 0 0 1-.13.587 1.333 1.333 0 0 1-.58.75c-.168.11-.342.151-.516.17-.166.017-.367.017-.6.017H9.41c.184-.218.32-.458.415-.698a2.562 2.562 0 0 0 .176-.864c.015-.238.015-.521.015-.826V6.002c.37-.158.59-.41.703-.605.16-.275.202-.571.173-.828a1.367 1.367 0 0 0-.404-.823L8.242 1.57l.8-.775.053-.052.094-.1.268-.26.033-.031c.115-.114.26-.256.432-.314z" fill="white"/></svg>`,
  // 상업용건물/지식산업센터 (241031: building-04)
  commercial: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.108 1.333h3.784c.352 0 .655 0 .905.02.263.022.526.07.778.198.376.192.682.498.874.874.128.252.176.515.198.778.02.25.02.553.02.905V13.333h.667c.368 0 .666.299.666.667s-.298.667-.666.667H2.667a.667.667 0 1 1 0-1.334h.666V4.108c0-.352 0-.655.02-.905.022-.263.07-.526.198-.778a1.667 1.667 0 0 1 .874-.874c.252-.128.515-.176.778-.198.25-.02.553-.02.905-.02zM5.333 4.667a.667.667 0 0 1 .667-.667h4a.667.667 0 1 1 0 1.333H6a.667.667 0 0 1-.667-.666zm0 2.666A.667.667 0 0 1 6 6.667h4a.667.667 0 1 1 0 1.333H6a.667.667 0 0 1-.667-.667zM6 9.333a.667.667 0 1 0 0 1.334h4a.667.667 0 1 0 0-1.334H6z" fill="white"/></svg>`,
  // 토지 (241030: table 4분할)
  land: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M5.333 2.4c0-.373 0-.56-.073-.703a.667.667 0 0 0-.291-.291C4.827 1.333 4.64 1.333 4.267 1.333h-.159c-.352 0-.655 0-.905.02-.263.022-.526.07-.778.198a1.667 1.667 0 0 0-.874.874c-.128.252-.176.515-.197.778-.02.25-.02.553-.02.905v.159c0 .374 0 .56.073.703.064.125.166.227.291.291.143.073.329.073.703.073h1.866c.374 0 .56 0 .703-.073a.667.667 0 0 0 .291-.291c.073-.143.073-.329.073-.703V2.4z" fill="white"/><path d="M2.4 6.667c-.373 0-.56 0-.703.072a.667.667 0 0 0-.291.292c-.073.142-.073.329-.073.702v4.16c0 .351 0 .654.02.904.022.263.07.526.198.778.19.376.497.683.874.874.252.128.515.176.778.198.25.02.553.02.905.02h.159c.373 0 .56 0 .703-.074a.667.667 0 0 0 .291-.291c.073-.143.073-.33.073-.702V7.733c0-.373 0-.56-.073-.703a.667.667 0 0 0-.291-.291c-.143-.072-.33-.072-.703-.072H2.4z" fill="white"/><path d="M6.667 13.6c0 .373 0 .56.072.703.064.125.166.227.291.291.143.073.33.073.703.073h4.16c.35 0 .654 0 .904-.02.263-.022.526-.07.778-.198.376-.19.683-.497.874-.874.128-.252.176-.515.198-.778.02-.25.02-.553.02-.905V7.733c0-.373 0-.56-.073-.703a.667.667 0 0 0-.291-.291c-.143-.072-.33-.072-.703-.072H7.733c-.373 0-.56 0-.703.072a.667.667 0 0 0-.291.291c-.072.143-.072.33-.072.703v5.867z" fill="white"/><path d="M14.667 4.267c0 .373 0 .56-.073.702a.667.667 0 0 1-.292.292c-.142.073-.329.073-.702.073H7.733c-.373 0-.56 0-.703-.073a.667.667 0 0 1-.291-.292c-.072-.142-.072-.329-.072-.702V2.4c0-.373 0-.56.072-.703a.667.667 0 0 1 .291-.291c.143-.073.33-.073.703-.073h4.16c.351 0 .654 0 .904.02.263.022.526.07.778.198.376.19.683.497.874.874.128.252.176.515.198.778.02.25.02.553.02.905v.159z" fill="white"/></svg>`,
  // 공장/창고 (241027: type-icon/공장·창고)
  factory: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M12.039 1.332h1.836c.195 0 .381.07.519.195a.685.685 0 0 1 .215.472v12c0 .177-.077.346-.215.471a.724.724 0 0 1-.519.195H2.125a.724.724 0 0 1-.52-.195.685.685 0 0 1-.214-.471V6.465a.685.685 0 0 1 .092-.346.724.724 0 0 1 .275-.254l2.938-1.533a.724.724 0 0 1 .373-.09.724.724 0 0 1 .728.69v.666l2.57-1.333a.724.724 0 0 1 .374-.09.724.724 0 0 1 .727.69v.666l1.102-.6V1.999c0-.177.077-.346.215-.472a.724.724 0 0 1 .52-.195h.734zM3.96 9.799a.467.467 0 0 1 .467-.467h1.27a.467.467 0 0 1 .466.467v1.866H3.961V9.799zm5.508 0a.467.467 0 0 1 .467-.467h1.27a.467.467 0 0 1 .466.467v1.866H9.469V9.799z" fill="white"/></svg>`,
  // 숙박시설 (241034: 침대형)
  hotel: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M.667 3.112c0-.216.086-.424.238-.576a.815.815 0 0 1 1.153 0c.153.153.238.36.238.576v5.296h4.889V5.556c0-.216.086-.424.239-.576a.815.815 0 0 1 .576-.239h4.074a4.074 4.074 0 0 1 4.074 4.074v4.889a.815.815 0 0 1-1.63 0v-1.222H2.296v1.222a.815.815 0 0 1-1.63 0V3.112zm4.481 4.481a1.833 1.833 0 1 0-2.593-2.593 1.833 1.833 0 0 0 2.593 2.593z" fill="white"/></svg>`,
  // 분양권 (241036: flag-02)
  presale: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3.333 1.333a.667.667 0 0 0-1.333 0V10v4.667a.667.667 0 1 0 1.333 0v-4.308c.046-.023.101-.048.165-.073.329-.132.91-.286 1.835-.286.854 0 1.574.281 2.419.619l.037.015c.813.325 1.748.7 2.878.7 1.075 0 1.827-.18 2.33-.381.252-.1.438-.206.57-.294a1.58 1.58 0 0 0 .237-.258c.13-.13.196-.302.196-.472V2.667a.667.667 0 0 0-.928-.472c-.006.004-.021.017-.049.035a1.53 1.53 0 0 1-.326.165c-.329.132-.91.286-1.835.286-.854 0-1.574-.281-2.42-.619l-.037-.014C7.398 1.708 6.462 1.333 5.333 1.333c-.861 0-1.515.115-2 .265V1.333z" fill="white"/></svg>`,
};

// ── 면적 선택 (백엔드 extract_main_area와 동일 로직, floor_area fallback 추가) ──
// randType별 대표 면적 우선순위:
//   토지(16): land → floor
//   아파트/오피스텔/연립/다세대/상가점포/사무실/지식산업센터(0,1,2,7,8,10,11,18)
//   + 다가구/상가주택(4,6) 전세·월세: supply → floor
//   그 외 (상업용건물, 단독, 공장 등): land → floor
function pickArea(randType, dealType, supplyArea, landArea, floorArea) {
  const s = supplyArea || 0;
  const l = landArea || 0;
  const f = floorArea || 0;
  if (randType === 16) return l || f;
  if ([0, 1, 2, 7, 8, 10, 11, 18].includes(randType) || ([4, 6].includes(randType) && dealType !== 0)) {
    return s || f;
  }
  return l || f;
}

// ── 거래유형 라벨 ──
const DEAL_LABELS = { 0: "매매", 1: "전세", 2: "월세" };

function getDealLabel(dealType) {
  return DEAL_LABELS[dealType] || "매매";
}

// ── 가격 포맷 ──
function formatMarkerPrice(val, dealType, deposit, monthly) {
  if (dealType === 2 && deposit != null && monthly != null) {
    return `${fmtShort(deposit)}/ ${fmtShort(monthly)}`;
  }
  return fmtShort(val);
}

function fmtShort(val) {
  if (val == null || val === 0) return "0";
  // val 단위: 만원. 1조 = 100,000,000만, 1억 = 10,000만
  if (val >= 100_000_000) {
    const jo = Math.floor(val / 100_000_000);
    const eokRem = Math.floor((val % 100_000_000) / 10_000);
    return eokRem > 0 ? `${jo}조 ${eokRem}억` : `${jo}조`;
  }
  if (val >= 10_000) {
    const eok = val / 10_000;
    return Number.isInteger(eok) ? `${eok}억` : `${eok.toFixed(1)}억`;
  }
  return `${val.toLocaleString()}만`;
}

// ── 클러스터 마커 ──
// 피그마: rgba(31,73,104,0.6), Gmarket Sans Bold
// 크기: 2-9개 → 36px(13px), 10-99개 → 44px(14px), 100+ → 54px(14px)

function clusterHTML(count, dataAttrs, clicked = false) {
  let size, fontSize;
  if (count < 10) { size = 36; fontSize = 13; }
  else if (count < 100) { size = 44; fontSize = 14; }
  else { size = 54; fontSize = 14; }

  const r = size / 2;
  const stroke = 2;

  // Focused: #ebf2f7 배경 + #29618b 텍스트 + 2px solid 테두리
  // Normal: rgba(31,73,104,0.6) 배경 + 흰 텍스트 + 2px solid #C2D7E7 테두리
  const circleFill = clicked ? "#ebf2f7" : "rgb(31,73,104)";
  const fillOpacity = clicked ? "1" : "0.6";
  const strokeColor = clicked ? "#29618b" : "#C2D7E7";
  const strokeAttr = `stroke="${strokeColor}" stroke-width="${stroke}"`;
  const circleR = r - stroke / 2;
  const textFill = clicked ? "#29618b" : "white";

  return `
    <div class="pin_container stage-pin disco-explore-cluster" ${dataAttrs} style="cursor:pointer;">
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${r}" cy="${r}" r="${circleR}" fill="${circleFill}" fill-opacity="${fillOpacity}" ${strokeAttr}/>
        <text x="${r}" y="${r}" text-anchor="middle" dominant-baseline="central" fill="${textFill}" font-size="${fontSize}" font-family="GmarketSansBold_, Noto Sans KR, sans-serif" font-weight="700">${count}</text>
      </svg>
    </div>
  `;
}

// ── 단일 매물 마커 (카드형) ──
function singleHTML(data, dataAttrs, isClicked = false) {
  const item = data.randType != null ? data : (data.list?.[0] || data);
  const estateType = item.randType ?? item.rand_type ?? item.e_rand_type ?? item.estate_type ?? 0;
  const s = getMarkerStyle(estateType);
  const dealType = item.dealType ?? item.deal_type ?? 0;
  const dealLabel = getDealLabel(dealType);
  const price = formatMarkerPrice(item.price ?? 0, dealType, item.deposit, item.monthly);
  const area = pickArea(estateType, dealType, item.supplyArea, item.landArea, item.floorArea);
  const areaText = area > 0 ? toArea(area, 0) : "";
  const icon = ICONS[s.icon] || ICONS.office;

  // 선택 전: 흰 테두리 + 가벼운 shadow
  // 선택 후: brand color 테두리 + 강한 shadow + overflow:hidden + 하단 shadow dot
  const borderColor = isClicked ? s.from : "white";
  const bubbleShadow = isClicked
    ? "0px 2px 8px rgba(0,0,0,0.12),0px 1px 4px rgba(0,0,0,0.08),0px 0px 1px rgba(0,0,0,0.08)"
    : "0px 2px 4px rgba(0,0,0,0.12),0px 1px 2px rgba(0,0,0,0.08),0px 0px 0.5px rgba(0,0,0,0.08)";
  // 선택 후는 overflow:hidden으로 gradient가 부모 radius에 클리핑됨
  // 선택 전은 overflow 없이 좌측 아이콘 영역에 직접 radius 적용
  const overflowStyle = isClicked ? "overflow:hidden;" : "";
  const iconAreaRadius = isClicked ? "0" : "10px 0 0 10px";

  // 말풍선 꼬리: 피그마 Polygon 5 (상단 모서리 곡선 처리된 폴리곤 + dy=5 drop shadow 포함)
  // 버블과 overlap -5px, SVG 자체에 shadow filter가 포함되어 있어 별도 shadow 불필요
  const tailSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="22" viewBox="0 0 25 22" fill="none"><g filter="url(#tail_shadow)"><path d="M13.7447 11.7215C12.947 12.8382 11.2874 12.8382 10.4897 11.7215L4.3761 3.16248C3.43057 1.83874 4.37682 3.56436e-07 6.00357 4.98651e-07L18.2308 1.56759e-06C19.8576 1.70981e-06 20.8038 1.83874 19.8583 3.16248L13.7447 11.7215Z" fill="white"/></g><defs><filter id="tail_shadow" x="0" y="0" width="24.2344" height="21.5586" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/><feOffset dy="5"/><feGaussianBlur stdDeviation="2"/><feComposite in2="hardAlpha" operator="out"/><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.07 0"/><feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/><feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/></filter></defs></svg>`;

  // 하단 shadow ellipse dot: 선택 후에만 보이지만, 항상 공간을 차지해야
  // 클릭 시 마커 높이가 변해 지도 앵커 기준으로 위로 튀는 현상을 방지
  const shadowDotVisibility = isClicked ? "visible" : "hidden";
  const shadowDot = `<div style="display:flex;justify-content:center;width:100%;margin-top:2px;visibility:${shadowDotVisibility};"><svg width="14" height="5" viewBox="0 0 14 5" fill="none"><ellipse cx="7" cy="2.5" rx="7" ry="2" fill="rgba(0,0,0,0.10)"/></svg></div>`;

  return `
    <div class="pin_container stage-pin" ${dataAttrs} style="display:inline-flex;flex-direction:column;align-items:flex-start;position:relative;cursor:pointer;">
      <div style="display:flex;flex-direction:row;align-items:stretch;border-radius:12px;border:2px solid ${borderColor};box-shadow:${bubbleShadow};margin-bottom:-5px;${overflowStyle}background:white;flex-shrink:0;">
        <div style="width:30px;display:flex;align-items:center;justify-content:center;border-radius:${iconAreaRadius};background:linear-gradient(180deg,${s.from} 0%,${s.to} 100%);position:relative;flex-shrink:0;align-self:stretch;">
          ${icon}
          <div style="position:absolute;inset:0;border-radius:inherit;box-shadow:inset 0 0 2.5px rgba(255,255,255,0.18);pointer-events:none;"></div>
        </div>
        <div style="display:flex;flex-direction:column;justify-content:center;padding:5px 11px 5px 9px;flex-shrink:0;">
          <div style="display:flex;flex-direction:column;gap:2px;align-items:flex-start;">
            <div style="display:flex;gap:2px;align-items:center;white-space:nowrap;font-size:13px;line-height:0;">
              <span style="display:flex;flex-direction:column;justify-content:center;font-weight:700;color:rgba(46,47,51,0.88);"><span style="line-height:1.4;">${dealLabel}</span></span>
              <span style="display:flex;flex-direction:column;justify-content:center;font-weight:800;color:#171719;"><span style="line-height:1.4;">${price}</span></span>
            </div>
            ${areaText ? `<div style="line-height:0;white-space:nowrap;"><span style="font-size:12px;color:rgba(55,56,60,0.61);letter-spacing:0.3px;line-height:16px;">${areaText}</span></div>` : ""}
          </div>
        </div>
      </div>
      <div style="display:flex;flex-direction:column;align-items:center;width:100%;">
        <div style="display:flex;justify-content:center;">${tailSVG}</div>
        ${shadowDot}
      </div>
    </div>
  `;
}

// ── 마커 클래스 (PC/Mobile 공통) ──

export class ExplorerMarker {
  static default(data) {
    const { count = 1, u, list = [] } = data;
    const suid = list[0]?.suid ?? data.suid ?? "";
    const hasOnlyOne = count == 1;

    if (hasOnlyOne && (list.length > 0 || data.randType != null)) {
      return singleHTML(
        data,
        `data-action="pin" data-type="single" data-status="default" data-uuid="${u}" data-suid="${suid}"`
      );
    }

    return clusterHTML(
      count || 1,
      `data-action="pin" data-type="single" data-status="default" data-uuid="${u}" ${hasOnlyOne ? `data-suid="${suid}"` : ""}`
    );
  }

  static clicked(data) {
    const { count = 1, u, list = [] } = data;
    const suid = list[0]?.suid ?? data.suid ?? "";
    const hasOnlyOne = count == 1;

    if (hasOnlyOne && (list.length > 0 || data.randType != null)) {
      return singleHTML(
        data,
        `data-action="pin" data-type="single" data-status="clicked" data-uuid="${u}" data-suid="${suid}"`,
        true
      );
    }

    return clusterHTML(
      count || 1,
      `data-action="pin" data-type="single" data-status="clicked" data-uuid="${u}"`,
      true
    );
  }

  static cluster(data) {
    const { count, u, map_lv } = data;
    return clusterHTML(
      count,
      `data-action="pin" data-type="cluster" data-status="default" data-uuid="${u}" data-map_lv="${map_lv}"`
    );
  }

  static cluster_clicked(data) {
    const { count, u, map_lv } = data;
    return clusterHTML(
      count,
      `data-action="pin" data-type="cluster" data-status="clicked" data-uuid="${u}" data-map_lv="${map_lv}"`,
      true
    );
  }
}

export const ExplorerMarkerMobile = ExplorerMarker;
